import React from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  MapPin, 
  Building2, 
  GraduationCap, 
  Train,
  ArrowRight,
  Landmark,
  TreePine
} from 'lucide-react';

const AboutDharwadPage = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-24 bg-navy-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D4AF37' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-navy-800/90 to-olive-900/80" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block text-gold-400 font-semibold text-sm tracking-wider uppercase mb-4">Our Location</span>
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
            Know More About <span className="text-gold-400">Dharwad</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Discover the educational hub of Karnataka where Jay Defence Academy is proudly located.
          </p>
        </div>
      </section>

      {/* Location Info */}
      <section className="py-20 bg-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-olive-600 font-semibold text-sm tracking-wider uppercase">Academy Location</span>
              <h2 className="text-4xl font-bold text-navy-900 mt-3 mb-6">Jay Defence Academy</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                Located in the heart of Vidyagiri, our academy is situated on the 2nd Floor of Yeri Classes building. 
                This prime location offers easy accessibility and is surrounded by educational institutions, making it 
                an ideal environment for focused learning and preparation.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-md">
                  <div className="w-12 h-12 rounded-xl bg-navy-900 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-gold-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-navy-900 mb-1">Address</h4>
                    <p className="text-gray-600">2nd Floor, Yeri Classes, Vidyagiri, Dharwad, Karnataka</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-md">
                  <div className="w-12 h-12 rounded-xl bg-navy-900 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-6 h-6 text-gold-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-navy-900 mb-1">Landmark</h4>
                    <p className="text-gray-600">Yeri Classes Building, Near KCD Circle</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-navy-900 rounded-3xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-6">Why Dharwad?</h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <GraduationCap className="w-8 h-8 text-gold-400 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gold-400 mb-1">Educational Hub</h4>
                    <p className="text-gray-300 text-sm">Home to prestigious institutions like Karnatak University and multiple engineering colleges.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Train className="w-8 h-8 text-gold-400 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gold-400 mb-1">Well Connected</h4>
                    <p className="text-gray-300 text-sm">Excellent connectivity via railway and road. Hubli Airport is just 25 km away.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Landmark className="w-8 h-8 text-gold-400 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gold-400 mb-1">Rich Heritage</h4>
                    <p className="text-gray-300 text-sm">A city known for its cultural heritage, classical music tradition, and historical significance.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <TreePine className="w-8 h-8 text-gold-400 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gold-400 mb-1">Peaceful Environment</h4>
                    <p className="text-gray-300 text-sm">Serene atmosphere perfect for focused study and preparation away from big city distractions.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Dharwad City */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">About Dharwad City</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Dharwad is a vibrant city in Karnataka known for its rich educational heritage and cultural significance.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-xl bg-olive-100 flex items-center justify-center mb-6">
                  <GraduationCap className="w-7 h-7 text-olive-600" />
                </div>
                <h3 className="text-xl font-bold text-navy-900 mb-3">Education Capital</h3>
                <p className="text-gray-600 leading-relaxed">
                  Often called the "Oxford of Karnataka," Dharwad is home to Karnatak University and numerous colleges, 
                  making it a preferred destination for students from across the country.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-xl bg-gold-100 flex items-center justify-center mb-6">
                  <Landmark className="w-7 h-7 text-gold-600" />
                </div>
                <h3 className="text-xl font-bold text-navy-900 mb-3">Cultural Heritage</h3>
                <p className="text-gray-600 leading-relaxed">
                  Known for Hindustani classical music and literature, Dharwad has produced legendary musicians 
                  and scholars who have contributed significantly to Indian culture.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-xl bg-navy-100 flex items-center justify-center mb-6">
                  <TreePine className="w-7 h-7 text-navy-600" />
                </div>
                <h3 className="text-xl font-bold text-navy-900 mb-3">Strategic Location</h3>
                <p className="text-gray-600 leading-relaxed">
                  Located in North Karnataka, Dharwad enjoys excellent connectivity. The twin city Hubli-Dharwad 
                  is a commercial and educational hub with modern amenities.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gold-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-navy-900 mb-4">Visit Us at Dharwad</h2>
          <p className="text-navy-800 mb-8 max-w-2xl mx-auto">
            Experience world-class defence preparation in the heart of Karnataka's education capital.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-navy-900 hover:bg-navy-800 text-white font-bold text-lg px-10 py-6 transition-all duration-300 hover:shadow-xl group">
              Contact Us Today
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default AboutDharwadPage;
